import { PrivacyScreen } from '@capacitor/privacy-screen';
import { Capacitor } from '@capacitor/core';
import ScreenProtection from '@/plugins/ScreenProtection';

/**
 * Toggle screenshot protection on native platforms
 * 
 * iOS: Uses custom native plugin for role-based protection
 * Android: Uses Capacitor Privacy Screen plugin for role-based protection
 * Web: No-op (screenshots cannot be prevented on web)
 * 
 * @param enabled - Whether to enable screenshot protection (true = block screenshots, false = allow)
 */
export const toggleScreenProtection = async (enabled: boolean): Promise<void> => {
  // Only apply on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('[ScreenProtection] Web platform detected, screenshot protection not applicable');
    return;
  }

  const platform = Capacitor.getPlatform();

  try {
    if (platform === 'ios') {
      // iOS: Use custom native plugin
      if (enabled) {
        await ScreenProtection.enable();
        console.log('[ScreenProtection] Screenshot protection ENABLED (iOS)');
      } else {
        await ScreenProtection.disable();
        console.log('[ScreenProtection] Screenshot protection DISABLED (iOS)');
      }
    } else if (platform === 'android') {
      // Android: Use Capacitor Privacy Screen plugin
      if (enabled) {
        await PrivacyScreen.enable({
          android: {
            dimBackground: true,
            preventScreenshots: true,
            privacyModeOnActivityHidden: 'splash'
          }
        });
        console.log('[ScreenProtection] Screenshot protection ENABLED (Android)');
      } else {
        await PrivacyScreen.disable();
        console.log('[ScreenProtection] Screenshot protection DISABLED (Android)');
      }
    }
  } catch (error) {
    console.warn('[ScreenProtection] Failed to toggle protection:', error);
    // Don't throw - this is a nice-to-have feature that shouldn't break the app
  }
};

/**
 * Enable screenshot protection (block screenshots)
 */
export const enableScreenProtection = async (): Promise<void> => {
  await toggleScreenProtection(true);
};

/**
 * Disable screenshot protection (allow screenshots)
 */
export const disableScreenProtection = async (): Promise<void> => {
  await toggleScreenProtection(false);
};
